/* ================================================================ */
// DATA ACTIONS - Content | Content.js
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import 'dotenv/config';
import PropTypes from 'prop-types';
// Constants
import { LoadingTypes } from '@myglobe/core/dist/types';
import { getMissingIds, getPagesById, getStoriesById, getPanelsById } from '../../selectors/Data';
// Data Action Helpers
import { callAPI, callAPISecurely, addData, mappify } from '.';
import { fetchStates } from './Mapping';
/* ========================================== */
// Actions
/* ========================================== */
// > Page Data
export const FETCH_PAGES = 'FETCH_PAGES';
export const FETCH_PAGES_FAILURE = 'FETCH_PAGES_FAILURE';
export const FETCH_PAGES_SUCCESS = 'FETCH_PAGES_SUCCESS';
// > Story Data
export const FETCH_STORIES = 'FETCH_STORIES';
export const FETCH_STORIES_FAILURE = 'FETCH_STORIES_FAILURE';
export const FETCH_STORIES_SUCCESS = 'FETCH_STORIES_SUCCESS';
export const CREATE_STORY = 'CREATE_STORY';
export const CREATE_STORY_SUCCESS = 'CREATE_STORY_SUCCESS';
export const UPDATE_STORY = 'UPDATE_STORY';
export const UPDATE_STORY_SUCCESS = 'UPDATE_STORY_SUCCESS';
export const ADD_STORY = 'ADD_STORY';
export const DELETE_STORY = 'DELETE_STORY';
export const DELETE_STORY_SUCCESS = 'DELETE_STORY_SUCCESS';
export const DELETE_STORY_FAILURE = 'DELETE_STORY_FAILURE';
// > Panel Data
export const FETCH_PANELS = 'FETCH_PANELS';
export const FETCH_PANELS_FAILURE = 'FETCH_PANELS_FAILURE';
export const FETCH_PANELS_SUCCESS = 'FETCH_PANELS_SUCCESS';
export const CREATE_PANEL = 'CREATE_PANEL';
export const CREATE_PANEL_SUCCESS = 'CREATE_PANEL_SUCCESS';
export const DELETE_PANEL = 'DELETE_PANEL';
export const DELETE_PANEL_FAILURE = 'DELETE_PANEL_FAILURE';
export const DELETE_PANEL_SUCCESS = 'DELETE_PANEL_SUCCESS';
export const UPDATE_PANEL = 'UPDATE_PANEL';
export const UPDATE_PANEL_SUCCESS = 'UPDATE_PANEL_SUCCESS';
export const UPDATE_PANEL_ORDER = 'UPDATE_PANEL_ORDER';
export const UPDATE_PANEL_ORDER_FAILURE = 'UPDATE_PANEL_ORDER_FAILURE';
export const UPDATE_PANEL_ORDER_SUCCESS = 'UPDATE_PANEL_ORDER_SUCCESS';
export const PANEL_ACTION_FAILURE = 'PANEL_ACTION_FAILURE';//see if one fail will suffice
// > State data
export const UPDATE_VISSTATE = 'UPDATE_VISSTATE';
export const UPDATE_VISSTATE_SUCCESS = 'UPDATE_VISSTATE_SUCCESS';
export const UPDATE_VISSTATE_FAILURE = 'UPDATE_VISSTATE_SUCCESS';
// > Vis Layers
export const FETCH_DODGY_VISLAYERS = 'FETCH_DODGY_VISLAYERS';
export const UPDATE_DODGY_VISLAYERS = 'UPDATE_DODGY_VISLAYERS';



/* ========================================== */
// Action Functions
/* ========================================== */
/* ========================================== */
/**
 * Fetch Pages
 * -----------
 * Fetches pages from db according to specified
 * page ids
 * @param {array} pageUUIDs
 */
export function fetchPages(pageUUIDs) {
    return (dispatch, getState) => {
        dispatch({ type: FETCH_PAGES, pages: pageUUIDs });

        var fetchPageUUIDs = pageUUIDs;
        // Check loading mode
        /*if(process.env.DATA_LOAD_MODE === LoadingTypes.SEQUENTIAL) {
            // Get current story ids
            const currentPageIds = getPagesByUUID(getState());
            // Check all story ids are present in loaded stories
            const loadedPageUUIDs = pageUUIDs.filter(uuid=>currentPageIds.includes(uuid));
            // Remaining stories check
            fetchPageUUIDs = pageUUIDs.filter(uuid=>!loadedPageUUIDs.includes(uuid));
        }*/

        // External Endpoint Fetch
        if (fetchPageUUIDs.length > 0) {
            let query = '{';
            fetchPageUUIDs.forEach((uuid, i) => query += `page${i}: page(id: "${uuid}") {
                uuid: id
                name
                slug
                type
                content
                style
                globeId
            }\n`);
            query += '}';

            return callAPI(query)
                .then(response => {
                    const { data } = response;
                    // Format page data to array
                    const formattedData = Object.values(data).map(d => ({ ...d, type: d.type.toLowerCase() }));
                    // Convert to page slug id map
                    const payload = mappify('slug', formattedData);
                    dispatch(addData('pages', payload));
                    // TODO: ASYNC SUCCESS HANDLING
                    dispatch({ type: FETCH_PAGES_SUCCESS });
                })
                .catch(error => {
                    console.warn(error);
                    dispatch({ type: FETCH_PAGES_FAILURE, error });
                });
        }
    };
}
fetchPages.propTypes = {
    pageUUIDs: PropTypes.arrayOf(PropTypes.string).isRequired
};
/* ========================================== */
/**
 * Fetch Stories
 * -------------
 * Fetches stories from db according to specified
 * story ids
 *
 * @param {array} storyUUIDs story uuids
 */
export function fetchStories(storyUUIDs) {
    return (dispatch, getState) => {
        dispatch({ type: FETCH_STORIES, stories: storyUUIDs });

        var fetchStoryUUIDs = storyUUIDs;
        // Check loading mode
        //TODO: Convert from slug id to uuid via selector here
        /*if(process.env.DATA_LOAD_MODE === LoadingTypes.SEQUENTIAL) {
            // Get current story ids
            const currentStoryUUIDs = getStoriesById(getState());
            // Check all story ids are present in loaded stories
            const loadedStoryUUIDs = storyUUIDs.filter(uuid=>currentStoryUUIDs.includes(uuid));
            // Remaining stories check
            fetchStoryUUIDs = storyUUIDs.filter(uuid=>!loadedStoryUUIDs.includes(uuid));
        }*/

        // External Endpoint Fetch
        if (fetchStoryUUIDs.length > 0) {
            let query = '{';
            fetchStoryUUIDs.forEach((uuid, i) => query += `story${i}: story(id: "${uuid}") {
                uuid: id
                title
                slug
                panels: storyPanels {
                    nodes {
                        panelId
                    }
                }
                order
                globeId
            }\n`);
            query += '}';

            return callAPI(query)
                .then(response => {
                    const { data } = response;
                    // Format story data with panels id array
                    const formattedData = Object.values(data).map(story => {
                        const formattedItem = {
                            ...story,
                            panels: story.panels.nodes.map(node => node.panelId)
                        };
                        return formattedItem;
                    });
                    // Convert to story id map
                    const payload = mappify('slug', formattedData);
                    // Get all panel ids in stories as a flat set
                    const panelUUIDs = [...new Set(Object.values(payload).map(story => story.panels).flat())];
                    const getUnloadedIds = getMissingIds('panels', panelUUIDs);
                    const missingPanels = getUnloadedIds(getState());
                    if (missingPanels.length > 0)
                        dispatch(fetchPanels(missingPanels));

                    dispatch(addData('stories', payload));
                    // TODO: ASYNC SUCCESS HANDLING
                    dispatch({ type: FETCH_STORIES_SUCCESS });
                })
                .catch(error => {
                    console.warn(error);
                    dispatch({ type: FETCH_STORIES_FAILURE, error });
                });
        }
    };
}
fetchStories.propTypes = {
    panelUUIDs: PropTypes.arrayOf(PropTypes.string).isRequired
};
/* ========================================== */
/**
 * Fetch Panels
 * ------------
 * Retrieves panels from a given
 * story subcollection and saves to state
 *
 * @param {array} panelIDs list of panel ids
 */
export function fetchPanels(panelUUIDs) {
    return (dispatch, getState) => {
        //const db = getState().Data.config;
        dispatch({ type: FETCH_PANELS });

        var fetchPanelIds = panelUUIDs;
        // Check loading mode
        if (process.env.DATA_LOAD_MODE === LoadingTypes.SEQUENTIAL) {
            // Get current panel ids
            const currentPanelIds = getPanelsById(getState());
            // Check all panel ids are present in loaded panels
            const loadedPanelUUIDs = panelUUIDs.filter(uuid => currentPanelIds.includes(uuid));
            // Remaining panels check
            fetchPanelIds = panelUUIDs.filter(uuid => !loadedPanelUUIDs.includes(uuid));
        }

        if (fetchPanelIds.length > 0) {
            // External Endpoint Fetch
            let query = '{';
            fetchPanelIds.forEach((uuid, i) => query += `panel${i}: panel(id: "${uuid}") {
                uuid: id
                slug
                title
                type
                background
                sections
                content
                position
                config
                state {
                    uuid: id
                    name
                    viewState
                    id
                }
                interactions
                styles
            }\n`);
            query += '}';

            return callAPI(query)
                .then(response => {
                    const { data } = response;
                    console.log("###",data);
                    let stateMap = {};
                    const payload = Object.values(data).reduce((map, obj) => {
                        map[obj.uuid] = {
                            ...obj,
                            state: obj.state.name,
                            viewState: obj.state.viewState,
                            viewStateId: obj.state.id,
                            //config: JSON.parse(obj.config) || null,
                            //sections: JSON.parse(obj.sections),
                            //styles: JSON.parse(obj.styles) || null
                        };
                        stateMap[obj.state.name] = obj.state.uuid;
                        return map;
                    }, {});

                    // Find and retrieve missing states
                    const stateIds = [...new Set(Object.values(payload).map(story => story.state))];
                    const getUnloadedIds = getMissingIds('states', stateIds);
                    const missingStates = getUnloadedIds(getState());
                    const missingStateUUIDs = missingStates.map(uuid => stateMap[uuid]);
                    console.debug('Data > Content (Actions) | ', 'fetchPanels -', 'missingStates:', missingStates, 'missingStateUUIDs:', missingStateUUIDs);
                    if (missingStateUUIDs.length > 0)
                        dispatch(fetchStates(missingStateUUIDs));

                    dispatch(addData('panels', payload));
                    // TODO: ASYNC SUCCESS HANDLING
                    dispatch({ type: FETCH_PANELS_SUCCESS });
                })
                .catch(error => {
                    console.warn('Data > Content (Actions) | ', 'fetchPanels -', error);
                    dispatch({ type: FETCH_PANELS_FAILURE, error });
                });
        }
    };
}
fetchPanels.propTypes = {
    panelUUIDs: PropTypes.arrayOf(PropTypes.string).isRequired
};
/* ========================================== */
/**
 * CREATE Story
 * ------------
 * CREATE story and return its id
 *
 *
 * @param {object} story the story object with title slug but no panels
 */
export function createStory(story, globeId) {
    return (dispatch, getState) => {
        dispatch({ type: CREATE_STORY });
        console.log(globeId);
        //potentially make these props dynamic if needed
        let query = `mutation CreateStoryMutation($globeId:UUID!, $title:String, $slug:String!) {
            __typename
            createStory(input: {
                title: $title,
                slug: $slug
            }) {
              story {
                uuid: id
                title
                slug
                panels
              }
            }
        }`;
        let variables = {
            "title": story.title,
            "slug": story.slug,
            globeId
        };
        return callAPISecurely(query, variables).then(response => {
            console.log(response);
            let story = response.data.createStory.story;
            dispatch({ type: CREATE_STORY_SUCCESS, payload: story });
        });
    };
}
/* ========================================== */
/**
 * UPDATE Story
 * ------------
 * Updates story by UUID & Globe Name
 *
 *
 * @param {object} story the story object with title slug panels props
 */
export function updateStory(story, globeName) {
    return (dispatch, getState) => {
        dispatch({ type: UPDATE_STORY });
        //potentially make these props dynamic if needed
        let query = `mutation UpdateStory($id: UUID!, $title:String, $slug:String) {
            updateStoryById(input: {storyPatch: {title: $title, slug: $slug}, id: $id}) {
            story {
                uuid: id
                title
                slug
            }
        }}`;
        // could maybe just use the panel object here rather than assigning?
        let variables = {
            "id": story.uuid,
            "title": story.title,
            "slug": story.slug
        };
        return /*callAPISecurely(query, variables)*/callAPISecurely(query, variables).then(response => {
            let story = response.data.updateStoryById.story;
            dispatch({ type: UPDATE_STORY_SUCCESS, payload: story });
        });
    }
}
/* ========================================== */
/**
 * DELETE Story
 * ------------
 * Delete story and associated panels
 *
 *
 * @param {uuid} panelId the panel ID
 * @param {uuid} storyId the story ID
 */
export function deleteStory(storyId) {
    return (dispatch, getState) => {
        dispatch({ type: DELETE_STORY });
        console.log(storyId);
        let query = `mutation DeleteStoryMutation($storyId: UUID!) {            
            deletePanelsByStory(input: {deletedStoryId: $storyId}) {
                clientMutationId
            }
            deleteStoryById(input: {id: $storyId}) {
                clientMutationId
                story {
                  id
                }
            }
        }`;
        let variables = {
            storyId
        };
        return callAPISecurely(query, variables).then(response => {
            console.log(response);
            const deletedStoryUUID = response.data.deleteStoryById?.story?.id;
            if(deletedPanelUUID)
                dispatch({ type: DELETE_STORY_SUCCESS, payload: deletedStoryUUID });
            else 
                dispatch({type: DELETE_STORY_FAILURE, payload: response.error});
        });
    };
}
/* ========================================== */
/**
 * UPDATE Panel
 * ------------
 * Updates panel by UUID & Globe Name
 *
 *
 * @param {object} panel the panel object with title slug content props
 */
export function updatePanel(panel, globeName,viewState) {
    return (dispatch, getState) => {
        dispatch({ type: UPDATE_PANEL });
        console.log("###PANEL",panel,viewState);
        //potentially make these props dynamic if needed
        let query = `mutation UpdatePanel($id: UUID!, $title:String, $slug:String, $content:String, $viewStateId:UUID!, $viewState:JSON) {
            updatePanelById(input: {panelPatch: {title: $title, slug: $slug, content: $content,
                visStateToStateId: {updateById: {visStatePatch: {viewState: $viewState}, id: $viewStateId}}},
                id: $id}) {
            panel {
                uuid: id
                title
                slug
                content
                position
            }
        }}`;
        let variables = {
            "id": panel.uuid,
            "title": panel.title,
            "slug": panel.slug,
            "position": panel.position,
            "content": panel.content,
            "viewState":viewState,
            "viewStateId":panel.viewStateId
        };
        return /*callAPISecurely(query, variables)*/callAPISecurely(query, variables).then(response => {
            let { panel } = response.data.updatePanelById;
            dispatch({ type: UPDATE_PANEL_SUCCESS, payload: panel });
        });
    }
}
/* ========================================== */
/**
 * REORDER Panels
 * ------------
 * Reorder panels
 *
 *
 * @param {object} panels the array of panels requiring updating {@param id, @param position}
 */
export function updatePanelOrder(panels) {
    return (dispatch, getState) => {
        dispatch({ type: UPDATE_PANEL_ORDER });
        console.log(panels);
        let query = 'mutation ReorderPanelsMutation{';
        panels.forEach((panel, i)=>query += `post${i+1}: updatePanelById(input:{panelPatch:{position:${panel.position}} id:"${panel.uuid}"}) {
            panel {
               uuid: id
               title
               slug
               content
               position
            }
        }\n`);
        query+='}';
        console.log(query);
        return /*callAPISecurely(query)*/callAPISecurely(query).then(response => {
            console.log(response);
            if(response.error)
                dispatch({type: UPDATE_PANEL_ORDER_FAILURE, payload: response.error});
            else {
                const panels = Object.keys(response.data).map(key=>response.data[key].panel);
                dispatch({ type: UPDATE_PANEL_ORDER_SUCCESS, payload: panels });
            }
        });
    };
}
/* ========================================== */
/**
 * CREATE Panel
 * ------------
 * CREATE panel return its id
 *
 *
 * @param {object} panel the panel object with title slug content props
 */
export function createPanel(panel, storyId, globeId) {
    return (dispatch, getState) => {
        dispatch({ type: CREATE_PANEL });
        console.log(panel, storyId, globeId);
        //potentially make these props dynamic if needed
        let query = `mutation CreatePanelMutation($storyId:UUID!, $globeId:UUID!, $title:String, $slug:String!, $content:String, $position:Int) {
            __typename
            createPanel(input: {
                panel: {storyPanelsUsingId: {
                    create: {storyId: $storyId}
                },
                    content: $content,
                    slug: $slug,
                    title: $title,
                    position: $position,
                    visStateToStateId: {
                        create: {
                            name: $slug,                            
                            viewState: "{}",
                            globeId: $globeId
                        }
                    }
                }
            }) {
              panel {
                uuid: id
                title
                slug
                content
                position
              }
            }
        }`;
        let variables = {
            "title": panel.title,
            "slug": panel.slug,
            "content": panel.content,
            "position": panel.position,
            globeId,
            storyId
        };
        return callAPISecurely(query, variables).then(response => {
            console.log(response);
            let panel = response.data.createPanel.panel;
            dispatch({ type: CREATE_PANEL_SUCCESS, payload: panel });
        });
    };
}
/* ========================================== */
/**
 * DELETE Panel
 * ------------
 * Delete panel and associated story relationship
 *
 *
 * @param {uuid} panelId the panel ID
 * @param {uuid} storyId the story ID
 */
export function deletePanel(panelId, storyId) {
    return (dispatch, getState) => {
        dispatch({ type: DELETE_PANEL });
        let query = `mutation DeletePanelMutation($storyId: UUID!, $panelId: UUID!) {
            deleteStoryPanelByStoryIdAndPanelId(input: {storyId: $storyId, panelId: $panelId}) {
              clientMutationId
              deletedStoryPanelId
            }
            deletePanelById(input: {id: $panelId}) {
              clientMutationId
              deletedPanelId
              panel {
                uuid: id
              }
            }
        }`;
        let variables = {
            panelId,
            storyId
        };
        return callAPISecurely(query, variables).then(response => {
            console.log(response);
            const deletedPanelUUID = response.data.deletePanelById?.panel?.uuid;
            if(deletedPanelUUID)
                dispatch({ type: DELETE_PANEL_SUCCESS, payload: deletedPanelUUID });
            else 
                dispatch({type: DELETE_PANEL_FAILURE, payload: response.error});
        });
    };
}


/* ========================================== */
/**
 * UPDATE VisState
 * ------------
 * Updates panel by UUID & Globe Name
 *
 *
 * @param {object} panel the panel object with title slug content props
 */
export function updateVisState(visState) {
    return (dispatch, getState) => {
        dispatch({ type: UPDATE_VISSTATE });
        //potentially make these props dynamic if needed
        let query = `mutation updateVisStateById($UpdateVisStateByIdInput: UpdateVisStateByIdInput!) {
            __typename
            updateVisStateById(input: $UpdateVisStateByIdInput) {
              clientMutationId
              visState {
                id
                globeId
                mapStyle
                name
                nodeId
                viewState
              }
            }  
        }`;
        // could maybe just use the panel object here rather than assigning?
        let variables = {
            "UpdateVisStateByIdInput": {
            "visStatePatch": visState, 
            "id":visState.id
          }
        };
        return /*callAPISecurely(query, variables)*/callAPISecurely(query, variables).then(response => {
            let { visState } = response.data.updateVisStateById;
            dispatch({ type: UPDATE_VISSTATE_SUCCESS, payload: visState });
        });
    }
}

/**
 * Fetch Vis Layers
 * -----------
 * Fetches vislayers from db according to specified
 * vis layer ids
 * @param {array} vislayerUUIDs
 */

 /* TODO: REMOVE DODGY ENDPOINTS AND MAKE A DB CALL */
// export function fetchVisLayers() {
//     return (dispatch, getState) => {
//         dispatch({ type: FETCH_VISLAYERS });

//         var geojsonUrl = 'https://api.myglobe.app/rousuploads?path=geojson';
//         var czmlUrl = 'https://api.myglobe.app/rousuploads?path=czml';
//         fetch(geojsonUrl) // Call the fetch function passing the url of the API as a parameter
//         .then((resp) => resp.json())
//         .then((data) => {
//             var datasetArray = data.map( (Id) => {
//                 var split = Id.split('/');
//                 var Name = split[split.length-1];
//                 var Title = Name.split('.')[0];
//                 var Id = 'https://rous.myglobe.app'+Id;
//                 return { Title, Name, Id, Display: true };
//             });
//             setDatasets(datasetArray);
//         })
//         .catch(function(e) {
//             //TODO #benjob
//             console.log("ERROR retrieving datasets:", e);
//         });

//         fetch(czmlUrl) // Call the fetch function passing the url of the API as a parameter
//         .then((resp) => resp.json())
//         .then((data) => {
//             var visArray = data.map( (Id) => {
//                 var split = Id.split('/');
//                 var Name = split[split.length-1];
//                 var Title = Name.split('.')[0];
//                 var Id = 'https://myglobe.app'+Id;
//                 return { style: "", Name, Filename: Name, Id, Display: true, Visible: false, type: "CZML" };                   
//             });
//             setVisLayers(visArray);
//         })
//         .catch(function(e) {
//             //TODO #benjob
//             console.log("ERROR retrieving datasets:", e);
//         });
        
//     };
// }
// fetchPages.propTypes = {
//     pageUUIDs: PropTypes.arrayOf(PropTypes.string).isRequired
// };

export function updateDodgyVislayers(visLayerArray) {
    return (dispatch, getState) => {
        dispatch({ type: UPDATE_DODGY_VISLAYERS, payload:visLayerArray });
    }
}